import React, { useCallback, useContext, useState } from 'react';

import { MDText } from 'i18n-react';

import { Button, IconComponentFunction, IconText, InfoIcon } from '@motorway/mw-highway-code';
import { SIZE, VARIANT } from '@motorway/mw-highway-code/enums';
import { Modal } from '@motorway/mw-highway-code/pre-alpha';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { EVENT } from 'Utilities/analytics/optimizely/constants';
import { useOptimizelyTrack } from 'Utilities/analytics/optimizely/useOptimizelyTrack';

import type { ServiceFeeTiersResponse } from 'Components/transactionFees/hooks/useServiceFeeData';

import { ServiceFeeExplainer } from '../ServiceFeeExplainer/ServiceFeeExplainer';
import Texts from '../transactionFees.texts.json';

import styles from './ServiceFeeAppliesNotice.scss';

const LocalT = new MDText(Texts);

type ServiceFeeAppliesNoticeProps = {
  feeAmountGBP?: number;
  label?: string;
  serviceFeeTiers?: ServiceFeeTiersResponse;
  showFee?: boolean;
  variant?: 'small' | 'regular';
};

export const ServiceFeeAppliesNotice: React.FC<ServiceFeeAppliesNoticeProps> = ({
  feeAmountGBP,
  label = LocalT.translate('serviceFeeDetailsWrapper.noticeLabel') as string,
  serviceFeeTiers,
  showFee = true,
  variant = 'regular',
}) => {
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { OPTIMIZELY_TRACK } = useOptimizelyTrack();

  const dpEnquiryId = vehicle?.enquiry?.dpEnquiryId;

  const openModal = useCallback(() => {
    OPTIMIZELY_TRACK(
      EVENT.SERVICE_FEE_EXPLAINER_MODAL_CLICKED,
      {
        customId: String(dpEnquiryId),
      },
      {
        action: feeAmountGBP ? 'valuation' : 'confirm_sale',
      },
    );
    setIsModalOpen(true);
  }, [OPTIMIZELY_TRACK, dpEnquiryId, feeAmountGBP]);

  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const InfoButton: IconComponentFunction = () => (
    <Button
      key="info"
      data-thc-icon
      data-testid="service-fee-notice-info-trigger"
      icon={() => <InfoIcon filled={false} size={variant === 'regular' ? SIZE.SMALL : SIZE.XSMALL} />}
      label={LocalT.translate('serviceFeeAppliesNotice.infoLabel') as string}
      onClick={openModal}
      showLabel={false}
      size={SIZE.SMALL}
      variant={VARIANT.TERTIARY}
    />
  );

  let content;
  if (!showFee) {
    content = LocalT.translate('serviceFeeAppliesNotice.noticeWithoutFee', { label });
  } else {
    const fee = feeAmountGBP
      ? `£${feeAmountGBP.toLocaleString()}`
      : '\u2007\u2007\u2007\u2007';
    content = LocalT.translate('serviceFeeAppliesNotice.noticeWithFee', { fee, label });
  }
  return (
    <div className={styles.component}>
      <IconText
        data-thc-icontext
        reverse
        data-testid="service-fee-applies-notice"
        icon={InfoButton}
        text={content}
        variant={`small--${variant}`}
      />
      {isModalOpen && (
        <Modal aria-labelledby='service-fee-modal-content' id='service-fee-modal' isOpen={isModalOpen} onClose={closeModal}>
          <ServiceFeeExplainer {...{ serviceFeeTiers }} />
        </Modal>
      )}
    </div>
  );
};
